import styles from "./styles.module.scss"
import { useEffect, useMemo, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import classNames from "classnames"
import ChevronRightOutline from "~icons/eva/chevron-right-outline"
import ChevronLeftOutline from "~icons/eva/chevron-left-outline"

class AnimatedTabsStore {
  pressedItemId: string
  width: number
  positionX: number

  constructor(initialItemId: string) {
    this.pressedItemId = initialItemId
    this.width = 0
    this.positionX = 0

    makeAutoObservable(this)
  }

  setPressedItemId(value: string) {
    this.pressedItemId = value
  }

  setWidth(value: number) {
    this.width = value
  }

  setPositionX(value: number) {
    this.positionX = value
  }
}

export const AnimatedTabs = observer(
  ({
    chosenItem,
    items,
    onPress,
    showArrows = false,
  }: {
    chosenItem: string
    items: { id: string; text: string }[]
    onPress: (itemId: string) => void
    showArrows?: boolean
  }) => {
    const elementRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
    const containerRef = useRef<HTMLDivElement>(null)
    const state = useMemo(() => new AnimatedTabsStore(chosenItem), [])
    const [showLeftButton, setShowLeftButton] = useState(false)
    const [showRightButton, setShowRightButton] = useState(true)

    useEffect(() => {
      // Initialize width and positionX based on the chosen item
      const chosenElement = elementRefs.current[chosenItem]
      if (chosenElement) {
        state.setWidth(chosenElement.offsetWidth)
        state.setPositionX(chosenElement.offsetLeft)
      }
    }, [chosenItem, elementRefs.current])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const checkScroll = () => {
        setShowLeftButton(container.scrollLeft > 0)
        setShowRightButton(container.scrollLeft < container.scrollWidth - container.clientWidth)
      }

      container.addEventListener("scroll", checkScroll)
      checkScroll() // Initial check

      return () => container.removeEventListener("scroll", checkScroll)
    }, [])

    const handleAnimation = (id: string) => {
      const element = elementRefs.current[id]
      if (element) {
        state.setWidth(element.offsetWidth)
        state.setPositionX(element.offsetLeft)
      }
    }

    const scroll = (direction: "left" | "right") => {
      if (!containerRef.current) return

      const container = containerRef.current
      const scrollAmount = container.offsetWidth / 1.9
      const newPosition =
        direction === "left"
          ? container.scrollLeft - scrollAmount
          : container.scrollLeft + scrollAmount

      container.scrollTo({
        left: newPosition,
        behavior: "smooth",
      })
    }

    return (
      <div className={showArrows ? styles.gridContainer : styles.flexContainer}>
        {showLeftButton ? (
          <button className={styles.scrollButton} onClick={() => scroll("left")}>
            <ChevronLeftOutline />
          </button>
        ) : (
          showArrows && <div className={styles.emptySpace} />
        )}
        <div ref={containerRef} className={styles.container}>
          {items.map(({ id, text }) => (
            <button
              key={id}
              ref={(el) => (elementRefs.current[id] = el)}
              onClick={() => {
                handleAnimation(id)
                onPress(id)
                state.setPressedItemId(id)
              }}
              className={classNames(
                styles.tabButton,
                id === chosenItem ? styles.isChosen : styles.notChosen,
              )}
            >
              {text}
            </button>
          ))}
          <div
            className={styles.animatedBar}
            style={{
              width: state.width,
              left: state.positionX,
            }}
          />
        </div>
        {showArrows && showRightButton ? (
          <button className={styles.scrollButton} onClick={() => scroll("right")}>
            <ChevronRightOutline />
          </button>
        ) : (
          showArrows && <div className={styles.emptySpace} />
        )}
      </div>
    )
  },
)
